import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import {
  FUND_REDEMPTION_RESULT_FIELDS,
  POSITION_RESULT_FIELDS,
  REDEEM_MODES,
  SUMMARY_RESULT_FIELDS,
} from '../calculator.constants';
import { shouldHideWithholdingTax } from '../calculator.utils';
import type { CalculatorState } from '../useCalculator';
import { CalculatorFundResultCard } from './calculatorFundResultCard';
import { CalculatorResultCurrencySelector } from './calculatorResultCurrencySelector';
import { CalculatorResultFields } from './calculatorResultFields';

const ResultFieldsBox = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: theme.borderRadius.sm,
        px: 2.5,
        py: 2,
        bgcolor: theme.color.surfaceContainer,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.625,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const MultipleFundResultsPlaceholder = () => {
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();

  return (
    <Box
      data-testid='calculator-fund-result-placeholder'
      sx={{
        borderRadius: theme.borderRadius.sm,
        px: 2.5,
        py: 2,
        bgcolor: theme.color.surfaceContainer,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='bodyMedium'
          sx={{ fontWeight: 500 }}
          color='textSecondary'
        >
          {t('calculator.selectFund')}
        </Typography>
        <ExpandMoreIcon
          fontSize='small'
          sx={{
            color: theme.color.primaryPressed,
          }}
        />
      </Box>
    </Box>
  );
};

export const CalculatorResultsPanel = ({
  calculator,
  forceAllExpanded = false,
  options,
}: {
  calculator: CalculatorState;
  forceAllExpanded?: boolean;
  options: Record<string, unknown>;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const {
    accountType,
    availableCurrencies,
    integrationResult,
    redeemMode,
    resultCurrency,
    setResultCurrency,
  } = calculator;
  const hideWithholdingTax = shouldHideWithholdingTax(accountType);
  const successData =
    integrationResult?.status === 'success'
      ? integrationResult.data
      : undefined;
  const showPerFundResults =
    redeemMode === REDEEM_MODES.MULTIPLE || redeemMode === REDEEM_MODES.ENTIRE;
  const positionDetailTitle =
    translateBackendString(options.positionDetailTitle) ||
    t('calculator.results.positionDetail');
  const summaryTitle =
    translateBackendString(options.summaryTitle) ||
    t('calculator.results.summary');
  const [expandedFundCodes, setExpandedFundCodes] = useState<string[]>([]);

  const resultFieldLabels = useMemo(
    () =>
      [...POSITION_RESULT_FIELDS, ...FUND_REDEMPTION_RESULT_FIELDS].reduce<
        Record<string, string>
      >((labels, field) => {
        labels[field.labelKey] = t(field.labelKey);
        return labels;
      }, {}),
    [t],
  );

  return (
    <Box data-testid='calculator-results-panel'>
      <Box
        data-pdf-section
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='headingXSmall' sx={{ fontWeight: 700 }}>
            {translateBackendString(options.resultsTitle) ||
              t('calculator.results.title')}
          </Typography>
          <CalculatorResultCurrencySelector
            availableCurrencies={availableCurrencies}
            resultCurrency={resultCurrency}
            setResultCurrency={setResultCurrency}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 700 }}>
            {positionDetailTitle}
          </Typography>
          {successData && availableCurrencies.length > 1 && (
            <Typography
              variant='bodySmall'
              data-testid='calculator-exchange-rate'
              sx={{ flexShrink: 0 }}
            >
              {t('calculator.results.exchangeRate', {
                rate: successData.exchangeRateCadToUsd,
              })}
            </Typography>
          )}
        </Box>
        {!successData && showPerFundResults && (
          <MultipleFundResultsPlaceholder />
        )}
        {!successData && !showPerFundResults && (
          <ResultFieldsBox>
            <CalculatorResultFields
              fields={POSITION_RESULT_FIELDS}
              hideWithholdingTax={hideWithholdingTax}
              options={options}
            />
          </ResultFieldsBox>
        )}
        {successData &&
          !showPerFundResults &&
          (successData.fundResults ?? []).map((fundResult) => (
            <ResultFieldsBox key={fundResult.fundCode}>
              <CalculatorResultFields
                fields={POSITION_RESULT_FIELDS}
                hideWithholdingTax={hideWithholdingTax}
                options={options}
                source={fundResult}
              />
            </ResultFieldsBox>
          ))}
      </Box>

      {successData && showPerFundResults && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {(successData.fundResults ?? []).map((fundResult) => (
            <Box
              key={fundResult.fundCode}
              data-pdf-section
              sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}
            >
              <CalculatorFundResultCard
                expanded={
                  forceAllExpanded ||
                  expandedFundCodes.includes(fundResult.fundCode)
                }
                fundName={`${fundResult.fundName} (${fundResult.fundCode})`}
                hideWithholdingTax={hideWithholdingTax}
                labels={resultFieldLabels}
                options={options}
                source={fundResult}
                onToggle={() =>
                  setExpandedFundCodes((previous) =>
                    previous.includes(fundResult.fundCode)
                      ? previous.filter((code) => code !== fundResult.fundCode)
                      : [...previous, fundResult.fundCode],
                  )
                }
              />
            </Box>
          ))}
        </Box>
      )}

      <Box
        data-pdf-section
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}
      >
        <Typography variant='bodySmall' sx={{ fontWeight: 700 }}>
          {summaryTitle}
        </Typography>
        <ResultFieldsBox>
          <CalculatorResultFields
            fields={SUMMARY_RESULT_FIELDS}
            hideWithholdingTax={hideWithholdingTax}
            options={options}
            source={successData?.redemptionSummary}
          />
        </ResultFieldsBox>
      </Box>
    </Box>
  );
};
